import {
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography as MuiTypography,
} from '@mui/material'
import Modal from '../../../../components/Modal'
import Typography from '../../../../components/Typography'
import { colors } from '../../../../themes/colors'
import TestIds from '../../../../testIds'
import { useState } from 'react'
import apiClient from '../../../../services/api-client'
import { Quote, RejectQuotePayload } from '../../storage/types'
import { useSelector } from 'react-redux'
import { selectCurrentFarmerAccount } from '../../../azure-b2c/storage'
import { useNavigate } from 'react-router-dom'
import { Loader } from '../../../../components/Loader'
import TextField from '../../../../components/TextField'

interface QuoteDeclineModalProps {
  open: boolean
  handleClose: () => void
  quote: Quote | null
}

export const QuoteDeclineModal: React.FC<QuoteDeclineModalProps> = ({
  open,
  handleClose,
  quote,
}) => {
  const account = useSelector(selectCurrentFarmerAccount)
  const [submitting, setSubmitting] = useState(false)
  const [selection, setSelection] = useState('')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState(false)
  const navigate = useNavigate()

  const reject = async () => {
    try {
      setSubmitting(true)
      const result = await apiClient.post<Quote, RejectQuotePayload>(
        `/quotes/${quote?.id}/reject`,
        {
          accountNumber: String(account?.accountNumber),
          version: String(quote?.version),
          reason: selection,
          notes: notes === '' ? undefined : notes,
        },
      )
      if (result && result.status === 200 && result.data) {
        // notify('Quote Rejected')
        navigate('/quotes')
      } else {
        setError(true)
      }
    } catch {
      setError(true)
    } finally {
      setSubmitting(false)
    }
  }

  const handleSetNotes: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (
    event,
  ) => {
    setNotes(event.target.value)
  }

  return (
    <Modal
      title='Decline Quote'
      open={open}
      handleClose={() => {
        handleClose()
        setSelection('')
        setNotes('')
      }}
      header={<Typography> Why are you declining this quote?</Typography>}
      footer={
        submitting ? (
          <Loader type='inline-flex' title='Submitting' />
        ) : (
          <Button
            disabled={submitting || !selection}
            variant='contained'
            // sx={{ width: '100%' }}
            onClick={reject}
            data-testid={TestIds.feat.quotes.declineOffer.reject}
          >
            Decline Quote
          </Button>
        )
      }
    >
      <RadioGroup
        sx={{
          display: 'flex',
          flexDirection: 'column',
          mb: 2,
        }}
        data-id={TestIds.feat.quotes.declineOffer.radioGroup}
        onChange={(e) => setSelection(e.currentTarget.value)}
      >
        <FormControlLabel
          slotProps={{
            typography: {
              color: colors.grey800,
            },
          }}
          control={<Radio />}
          value='Too expensive'
          label='Too expensive'
          data-id={TestIds.feat.quotes.declineOffer.tooExpensive}
        />
        <FormControlLabel
          slotProps={{
            typography: {
              color: colors.grey800,
            },
          }}
          control={<Radio />}
          value='No longer need it'
          label='No longer need it'
          data-id={TestIds.feat.quotes.declineOffer.noLongerNeedIt}
        />
        <FormControlLabel
          slotProps={{
            typography: {
              color: colors.grey800,
            },
          }}
          control={<Radio />}
          value='Already purchased'
          label='Already purchased'
          data-id={TestIds.feat.quotes.declineOffer.alreadyPurchased}
        />
        <FormControlLabel
          slotProps={{
            typography: {
              color: colors.grey800,
            },
          }}
          control={<Radio />}
          value='Other'
          label='Other'
          data-id={TestIds.feat.quotes.declineOffer.other}
        />
      </RadioGroup>
      <TextField
        variant='outlined'
        value={notes}
        onChange={handleSetNotes}
        multiline
        minRows={3}
        label='Can you give us feedback on why you are declining this quote'
      />
      {error && (
        <MuiTypography variant='body2' color='error' sx={{ marginTop: '8px' }}>
          Something went wrong, please try again.
        </MuiTypography>
      )}
    </Modal>
  )
}
